import { useFeature } from '../auth/features';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { caseService, searchService } from '../services/api';
import { Briefcase, Plus, FileText, Calendar, Loader2, RefreshCw, ChevronRight, ShieldCheck, Activity, UploadCloud, Search, FileSpreadsheet, EyeOff, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useConfirmDialog } from '../components/DialogHost';
import {
    currentUserId, partitionCases, readHideArtifacts, recentCaseIdsFrom, writeHideArtifacts,
} from '../utils/caseArtifacts';
import { ForensicImportModal } from '../components/cases/ForensicImportModal';
import { CsvImportModal } from '../components/cases/CsvImportModal';
import { artifactFilterBtnActive, artifactFilterBtnIdle } from '../components/dashboard/dashboardStyles';
import { caseCanvasUrl, caseFileUrl } from '../case-canvas/landingTab';

export const Cases: React.FC = () => {
    const navigate = useNavigate();
    const { confirm, confirmDialog } = useConfirmDialog();
    const [cases, setCases] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showForensicModal, setShowForensicModal] = useState(false);
    const [showCsvImportModal, setShowCsvImportModal] = useState(false);
    // Persisted per browser; OFF unless the officer turned it on.
    const [hideArtifacts, setHideArtifacts] = useState(readHideArtifacts);
    const [showHiddenGroup, setShowHiddenGroup] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [recentRows, setRecentRows] = useState<any[]>([]);
    const [searchTerm, setSearchTerm] = useState('');

    // Retro-scan is super_admin-only on the API (require_super_admin).
    const isSuperAdmin = useFeature('admin.super');

    // Form State
    const [formData, setFormData] = useState({
        title: '',
        fir_number: '',
        officer_name: '',
        notes: ''
    });

    useEffect(() => {
        loadCases();
    }, []);

    const loadCases = async () => {
        setLoadError(false);
        try {
            const data = await caseService.getAll();
            setCases(Array.isArray(data) ? data : []);
        } catch (error) {
            // Never read as "no cases": a failed list says so and offers Retry.
            console.error("Failed to load cases", error);
            setLoadError(true);
        } finally {
            setLoading(false);
        }
        // Best effort: recently viewed cases are exempt from the artifact filter.
        try {
            const rows = await searchService.getRecentlyViewed(100, 'case');
            setRecentRows(Array.isArray(rows) ? rows : []);
        } catch { /* the owner exemption still applies */ }
    };

    const toggleHideArtifacts = () => {
        const next = !hideArtifacts;
        writeHideArtifacts(next);
        setHideArtifacts(next);
        setShowHiddenGroup(false);
    };

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const created = await caseService.create(formData);
            const id = String(created?.id || created?.case_id || '');
            setShowModal(false);
            setFormData({ title: '', fir_number: '', officer_name: '', notes: '' });
            loadCases();
            if (id) {
                toast.success('Case created successfully', {
                    action: {
                        label: 'Open Fusion Canvas',
                        onClick: () => navigate(caseCanvasUrl(id, 'build')),
                    },
                });
            } else {
                toast.success('Case created successfully');
            }
        } catch (error) {
            toast.error('Failed to create case');
        }
    };

    const handleRetroScan = async () => {
        if (!isSuperAdmin) {
            toast.error('Retro-scan requires a super_admin account.');
            return;
        }
        const shouldRun = await confirm({
            title: 'Run Retro-Active Intelligence Scan?',
            message: 'This will re-analyze all investigation graphs against the latest database of high-risk entities.',
            confirmText: 'Run Scan',
        });
        if (!shouldRun) return;

        setLoading(true);
        try {
            const result = await caseService.runRetroScan();
            if (result.alerts_found > 0) {
                toast.warning(`Found ${result.alerts_found} new high-risk matches in existing cases`, { duration: 6000 });
                console.warn("Retro-Scan Alerts:", result.alerts);
            } else {
                toast.success('Scan complete. No new threats detected in open cases.');
            }
        } catch (error) {
            console.error("Scan failed", error);
            if (axios.isAxiosError(error) && error.response?.status === 403) {
                toast.error('Retro-scan requires super_admin access.');
            } else if (axios.isAxiosError(error) && error.response?.status) {
                toast.error(`Retro-scan failed (${error.response.status}). Check backend logs.`);
            } else {
                toast.error('Retro-scan failed. Please check backend logs.');
            }
        } finally {
            setLoading(false);
            loadCases();
        }
    };

    const artifactCtx = useMemo(
        () => ({ currentUserId: currentUserId(), recentCaseIds: recentCaseIdsFrom(recentRows) }),
        [recentRows],
    );
    const term = searchTerm.trim().toLowerCase();
    // A search looks through every case; the artifact filter only shapes the unsearched list.
    const { visible: filteredCases, hidden: hiddenCases } = useMemo(() => {
        if (term) {
            const match = (c: any) => !!(
                c.title?.toLowerCase().includes(term) ||
                c.fir_number?.toLowerCase().includes(term) ||
                c.officer_name?.toLowerCase().includes(term)
            );
            return { visible: cases.filter(match), hidden: [] as any[] };
        }
        return partitionCases(cases, hideArtifacts, artifactCtx);
    }, [cases, term, hideArtifacts, artifactCtx]);

    const renderCard = (c: any, dimmed = false) => (
                        <div
                            key={c.id}
                            data-testid={dimmed ? 'case-card-hidden' : 'case-card'}
                            className={`bg-card border border-border rounded-xl p-5 hover:border-primary/30 transition-all group cursor-pointer hover:shadow-lg ${dimmed ? 'opacity-50 grayscale' : ''}`}
                            onClick={() => navigate(caseFileUrl(c.id))}
                        >
                            <div className="flex justify-between items-start mb-3">
                                <div className="p-2 bg-primary/10 rounded-lg">
                                    <FileText className="w-6 h-6 text-primary" />
                                </div>
                                <span className={`px-2 py-1 text-xs rounded font-medium ${c.status === 'OPEN' ? 'bg-green-500/10 text-green-400'
                                    : c.status === 'PROCESSING' ? 'bg-amber-500/10 text-amber-400'
                                    : c.status === 'CLOSED' ? 'bg-slate-700 text-muted-foreground'
                                        : 'bg-yellow-500/10 text-yellow-400'
                                    }`}>
                                    {c.status}
                                </span>
                            </div>

                            <h3 className="text-lg font-semibold text-foreground mb-0.5 break-words line-clamp-2">{c.title}</h3>
                            <p className="text-sm text-muted-foreground mb-3 font-mono">{c.fir_number || 'No FIR Number'}</p>

                            {/* Investigation & Evidence Counts */}
                            <div className="flex items-center gap-2 mb-3">
                                <span className="flex items-center gap-1 px-2 py-0.5 text-xs rounded bg-primary/10 text-foreground border border-primary/20">
                                    <Activity className="w-3 h-3" /> {c.investigation_count ?? 0} investigations
                                </span>
                                <span className="flex items-center gap-1 px-2 py-0.5 text-xs rounded bg-emerald-500/10 text-emerald-400">
                                    <ShieldCheck className="w-3 h-3" /> {c.evidence_count ?? 0} evidence
                                </span>
                            </div>

                            <div className="space-y-1.5 text-sm text-muted-foreground">
                                <div className="flex items-center gap-2">
                                    <Briefcase className="w-4 h-4" />
                                    <span>{c.officer_name || 'Unassigned'}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Calendar className="w-4 h-4" />
                                    <span>{new Date(c.created_at).toLocaleDateString()}</span>
                                </div>
                                {(c.creator_full_name || c.creator_username) && (
                                    <div className="flex items-center gap-2">
                                        <span className="px-1.5 py-0.5 text-[10px] rounded bg-primary/10 text-primary font-medium">CREATED BY</span>
                                        <span className="text-xs">{c.creator_full_name || c.creator_username}</span>
                                    </div>
                                )}
                            </div>

                            <div className="mt-4 pt-3 border-t border-border flex items-center justify-between">
                                <span className="text-sm text-primary group-hover:text-primary/80 flex items-center gap-1">
                                    View Case <ChevronRight className="w-4 h-4" />
                                </span>
                                <button
                                    type="button"
                                    aria-label="Open Fusion Canvas"
                                    title="Open Fusion Canvas"
                                    data-testid="case-card-open-canvas"
                                    onClick={(e) => { e.stopPropagation(); navigate(caseCanvasUrl(c.id)); }}
                                    className="p-1.5 rounded-md bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 transition-colors"
                                >
                                    <Sparkles className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
    );

    return (
        <div className={`p-6 max-w-7xl mx-auto flex flex-col`}>
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-foreground">Case Management</h1>
                    <p className="text-muted-foreground mt-1">Active Investigations and FIRs</p>
                </div>
                <div className="flex gap-2 flex-wrap items-center">
                    <div className="relative mr-2">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                        <input
                            type="text"
                            placeholder="Search cases..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="bg-secondary/30 border border-border/80 text-foreground text-sm rounded-lg pl-9 pr-4 py-2 w-64 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                        />
                    </div>
                    <button
                        onClick={toggleHideArtifacts}
                        data-testid="artifact-filter-toggle"
                        aria-pressed={hideArtifacts}
                        className={`px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium transition-colors ${hideArtifacts ? artifactFilterBtnActive : artifactFilterBtnIdle}`}
                        title="Hide cases marked as demo/test artifacts, or whose title starts with test, demo or fake. Cases you created or viewed in the last 30 days always stay."
                    >
                        {hideArtifacts && <EyeOff className="w-4 h-4 shrink-0" />}
                        {hideArtifacts ? 'Hiding demo/test cases' : 'Hide demo/test cases'}
                    </button>
                    {isSuperAdmin && (
                    <button
                        onClick={handleRetroScan}
                        className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium transition-colors"
                        title="Scan all cases for new threats (super_admin only)"
                    >
                        <RefreshCw className="w-4 h-4" /> Retro-Scan
                    </button>
                    )}
                    <button
                        onClick={() => setShowCsvImportModal(true)}
                        className="bg-secondary hover:bg-secondary/80 text-secondary-foreground px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium transition-colors border border-border"
                        title="Import wallet addresses from a .csv or .xlsx file"
                        data-tour="cases-csv-import"
                    >
                        <FileSpreadsheet className="w-4 h-4 text-green-500" /> Import CSV / Excel
                    </button>
                    <button
                        onClick={() => setShowForensicModal(true)}
                        className="bg-secondary hover:bg-secondary/80 text-secondary-foreground px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium transition-colors border border-border"
                        title="Import a forensic extraction file (.ufdr, .e01)"
                    >
                        <UploadCloud className="w-4 h-4 text-primary" /> Import Extraction
                    </button>
                    <button
                        onClick={() => setShowModal(true)}
                        className="bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium transition-colors"
                    >
                        <Plus className="w-4 h-4" /> New FIR / Case
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="flex-1 flex items-center justify-center text-muted-foreground">
                    <Loader2 className="w-8 h-8 animate-spin" />
                </div>
            ) : loadError ? (
                <div role="alert" data-testid="cases-load-error" className="flex-1 flex flex-col items-center justify-center text-muted-foreground border-2 border-dashed border-border rounded-xl m-4 p-6">
                    <Briefcase className="w-12 h-12 mb-4 opacity-50" />
                    <p className="text-foreground font-medium">Could not load cases</p>
                    <p className="text-sm mt-1">Your cases are not gone; the list did not load. Try again.</p>
                    <button
                        type="button"
                        data-testid="cases-load-retry"
                        onClick={() => { setLoading(true); loadCases(); }}
                        className="mt-4 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
                    >
                        Retry
                    </button>
                </div>
            ) : (
                <>
                    {filteredCases.length === 0 && hiddenCases.length === 0 ? (
                        <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground border-2 border-dashed border-border rounded-xl m-4 p-6">
                            <Briefcase className="w-12 h-12 mb-4 opacity-50" />
                            <p>{cases.length === 0 ? 'No cases yet.' : 'No cases found matching your criteria.'}</p>
                            {cases.length === 0 && (
                                <button onClick={() => setShowModal(true)} className="mt-4 text-primary hover:underline">Create your first case</button>
                            )}
                        </div>
                    ) : filteredCases.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {filteredCases.map((c) => renderCard(c))}
                        </div>
                    ) : null}
                    {hiddenCases.length > 0 && (
                        <div className="mt-6" data-testid="hidden-artifact-group">
                            <button
                                type="button"
                                data-testid="hidden-artifact-toggle"
                                aria-expanded={showHiddenGroup}
                                onClick={() => setShowHiddenGroup((v) => !v)}
                                className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-2"
                            >
                                <EyeOff className="w-4 h-4 shrink-0" />
                                {hiddenCases.length} artifact {hiddenCases.length === 1 ? 'case' : 'cases'} hidden, {showHiddenGroup ? 'hide' : 'show'}
                            </button>
                            {showHiddenGroup && (
                                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {hiddenCases.map((c) => renderCard(c, true))}
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}

            {/* Create Case Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-card border border-border/60 rounded-xl w-full max-w-md shadow-2xl overflow-hidden">
                        <div className="h-1 w-full bg-primary" />
                        <div className="p-6">
                        <h2 className="text-xl font-bold mb-4">Create New Case</h2>
                        <form onSubmit={handleCreate} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-muted-foreground mb-1">Case Title</label>
                                <input
                                    type="text"
                                    required
                                    className="w-full bg-card border border-border rounded p-2 text-foreground"
                                    placeholder="e.g. WazirX Hack Investigation"
                                    value={formData.title}
                                    onChange={e => setFormData({ ...formData, title: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-muted-foreground mb-1">FIR Number</label>
                                <input
                                    type="text"
                                    className="w-full bg-card border border-border rounded p-2 text-foreground"
                                    placeholder="e.g. FIR-2023-001"
                                    value={formData.fir_number}
                                    onChange={e => setFormData({ ...formData, fir_number: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-muted-foreground mb-1">Investigating Officer</label>
                                <input
                                    type="text"
                                    className="w-full bg-card border border-border rounded p-2 text-foreground"
                                    placeholder="Officer Name"
                                    value={formData.officer_name}
                                    onChange={e => setFormData({ ...formData, officer_name: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-muted-foreground mb-1">Notes</label>
                                <textarea
                                    className="w-full bg-card border border-border rounded p-2 text-foreground h-24"
                                    placeholder="Initial details..."
                                    value={formData.notes}
                                    onChange={e => setFormData({ ...formData, notes: e.target.value })}
                                />
                            </div>
                            <div className="flex justify-end gap-3 mt-6">
                                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 rounded-lg bg-brand-ink text-white hover:bg-neutral-900 dark:bg-white/10 dark:text-foreground transition-colors">Cancel</button>
                                <button type="submit" className="bg-primary hover:bg-brand-gold-hover text-primary-foreground px-4 py-2 rounded-lg font-medium transition-colors">Create Case</button>
                            </div>
                        </form>
                        </div>
                    </div>
                </div>
            )}

            {confirmDialog}

            <ForensicImportModal
                isOpen={showForensicModal}
                onClose={() => {
                    setShowForensicModal(false);
                    loadCases();
                }}
            />

            <CsvImportModal
                isOpen={showCsvImportModal}
                onClose={() => {
                    setShowCsvImportModal(false);
                    loadCases();
                }}
            />
        </div>
    );
};
